import { useCallback, useEffect, useRef, useState } from "react";
import { useThemeMode } from "../contexts/ThemeContext";
import { typographyTokens } from "../theme/tokens";
import "./CircularText.css";

type Props = {
  text: string;
  diameter?: number;
  fontSize?: number;
  spinDuration?: number;
  className?: string;
};

export default function CircularText({
  text,
  diameter = 44,
  fontSize = 10,
  spinDuration = 22,
  className = "",
}: Props) {
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === "dark";
  const textColor = isDark ? "#F4F7FB" : "#0B1220";

  const center = diameter / 2;
  const r = center - fontSize * 0.7;
  const letters = Array.from(text);
  const N = letters.length;

  // ── Physics-based rotation ────────────────────────────────────────
  const normalSpeed = 360 / spinDuration;
  const hoverSpeed = normalSpeed * 6;

  const rafRef = useRef(0);
  const angleRef = useRef(0);
  const velocityRef = useRef(normalSpeed);
  const targetVelRef = useRef(normalSpeed);
  const lastTimeRef = useRef(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [hovered, setHovered] = useState(false);

  const tick = useCallback(
    (ts: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = ts;
      const dt = Math.min((ts - lastTimeRef.current) / 1000, 0.1);
      lastTimeRef.current = ts;

      const accelerating = targetVelRef.current > velocityRef.current;
      const tau = accelerating ? 0.25 : 1.1;
      const lerp = 1 - Math.exp(-dt / tau);
      velocityRef.current += (targetVelRef.current - velocityRef.current) * lerp;

      angleRef.current = (angleRef.current + velocityRef.current * dt) % 360;

      // CSS transform на div — GPU-composited, без jitter
      if (wrapRef.current) {
        wrapRef.current.style.transform = `rotate(${angleRef.current}deg) translateZ(0)`;
      }

      rafRef.current = requestAnimationFrame(tick);
    },
    [],
  );

  useEffect(() => {
    targetVelRef.current = normalSpeed;
    velocityRef.current = normalSpeed;
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [normalSpeed, tick]);

  const onEnter = () => {
    setHovered(true);
    targetVelRef.current = hoverSpeed;
  };
  const onLeave = () => {
    setHovered(false);
    targetVelRef.current = normalSpeed;
  };

  return (
    <div
      className={`circular-text ${className}`}
      style={{
        width: diameter,
        height: diameter,
        cursor: "default",
        opacity: hovered ? 1 : 0.94,
        transition: "opacity 0.2s ease",
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      role="img"
      aria-label={text}
    >
      <div
        ref={wrapRef}
        style={{
          width: diameter,
          height: diameter,
          willChange: "transform",
          backfaceVisibility: "hidden",
        }}
      >
        <svg
          width={diameter}
          height={diameter}
          viewBox={`0 0 ${diameter} ${diameter}`}
          overflow="visible"
          style={{ display: "block" }}
        >
          {letters.map((ch, i) => {
            const angle = (360 / N) * i - 90;
            return (
              <text
                key={i}
                x={center}
                y={center - r}
                fill={textColor}
                fontSize={fontSize}
                fontWeight={typographyTokens.weights.bold}
                fontFamily={typographyTokens.fontFamily}
                textAnchor="middle"
                dominantBaseline="central"
                transform={`rotate(${angle} ${center} ${center})`}
              >
                {ch}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
